import { z } from "zod";

export const creatorStyleVisibilitySchema = z.enum(["draft", "internal", "public", "archived"]);
export const creatorStyleSortSchema = z.enum(["rank", "name"]);
export const creatorStyleCreatorTypeSchema = z.enum(["creator", "captions"]);
export const creatorStyleCategorySchema = z.enum([
  "Trending",
  "New",
  "Bold",
  "Polished",
  "Minimal",
  "Business",
  "Motivation",
  "Educational",
  "Entertainment",
  "Lifestyle",
]);

const creatorStyleRecordSchema = z.object({
  videoStyleId: z.string(),
  displayName: z.string(),
  creator: z.object({
    type: creatorStyleCreatorTypeSchema.default("creator"),
    displayName: z.string(),
    handle: z.string().nullish(),
    profileUrl: z.string().nullish(),
  }),
  reference: z.object({
    sourceCaptionsProjectId: z.string(),
    sourceCaptionsProjectSyncToken: z.string().nullish(),
    sourceOwnerUserId: z.string(),
    sourceOwnerOrgId: z.string().nullish(),
    libraryCaptionsProjectId: z.string(),
    libraryCaptionsProjectSyncToken: z.string().nullish(),
    libraryMirageProjectId: z.string(),
    libraryVideoId: z.string(),
    libraryOwnerUserId: z.string(),
    libraryOwnerOrgId: z.string(),
    projectName: z.string(),
    projectDataUris: z.array(z.string()),
    slug: z.string().optional(),
    customPreviewVideoAsset: z.string().nullish(),
    customThumbnailPreviewAsset: z.string().nullish(),
  }),
  availability: z.object({
    visibility: creatorStyleVisibilitySchema,
    mirageStudio: z.boolean(),
    ios: z.object({
      enabled: z.boolean(),
      minVersion: z.string().nullish(),
    }),
  }),
  previewVideoAsset: z.string().nullish(),
  thumbnailPreviewAsset: z.string().nullish(),
  rank: z.number().int(),
  categories: z.array(creatorStyleCategorySchema).default([]),
  processingStatus: z.enum(["ready", "processing", "failed"]),
  processingError: z.string().nullish(),
  autofillPrompt: z.string().default(""),
  marketingSummary: z.string().default(""),
  sampleDirectionPrompts: z
    .array(
      z.object({
        label: z.string(),
        prompt: z.string(),
        rank: z.number().int(),
      })
    )
    .default([]),
});

export const creatorStyleResponseSchema = z.object({
  style: creatorStyleRecordSchema,
  previewVideoUrl: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
});

export const listCreatorStylesParamsSchema = z.object({
  q: z.string().optional(),
  visibility: creatorStyleVisibilitySchema.optional(),
  sort: creatorStyleSortSchema.optional(),
});

export const listCreatorStylesResponseSchema = z.object({
  styles: z.array(creatorStyleResponseSchema),
});

export const importCreatorStyleRequestSchema = z.object({
  source: z.string().min(1),
  displayName: z.string().optional(),
});

export const updateCreatorStyleRequestSchema = z.object({
  displayName: z.string().optional(),
  slug: z.string().optional(),
  creatorType: creatorStyleCreatorTypeSchema.optional(),
  creatorDisplayName: z.string().optional(),
  creatorHandle: z.string().nullable().optional(),
  creatorProfileUrl: z.string().nullable().optional(),
  visibility: creatorStyleVisibilitySchema.optional(),
  mirageStudio: z.boolean().optional(),
  iosEnabled: z.boolean().optional(),
  iosMinVersion: z.string().nullable().optional(),
  rank: z.number().int().optional(),
  categories: z.array(creatorStyleCategorySchema).optional(),
  autofillPrompt: z.string().optional(),
  marketingSummary: z.string().optional(),
  sampleDirectionPrompts: z
    .array(z.object({ prompt: z.string().min(1), label: z.string().min(1) }))
    .optional(),
});

export const generateCreatorStylePreviewRequestSchema = z.object({
  customPreviewVideoAsset: z.string().nullable().optional(),
  customThumbnailPreviewAsset: z.string().nullable().optional(),
});

export const refreshCreatorStyleSourceRequestSchema = z.object({
  source: z.string().min(1).optional(),
  preserveCustomMedia: z.boolean(),
});

export type CreatorStyleResponse = z.infer<typeof creatorStyleResponseSchema>;
export type CreatorStyleCreatorType = z.infer<typeof creatorStyleCreatorTypeSchema>;
export type CreatorStyleCategory = z.infer<typeof creatorStyleCategorySchema>;
export type CreatorStyleVisibility = z.infer<typeof creatorStyleVisibilitySchema>;
export type CreatorStyleSort = z.infer<typeof creatorStyleSortSchema>;
